"use client";

import React from "react";
import { motion, useReducedMotion } from "framer-motion";
import { CalendarRange, ChartLine as LineChart, ShieldAlert, ArrowUpRight, Crown, Sun, CloudRain } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Link } from "@/components/common/Link";
const challenges = [{
  id: 1,
  title: "Seasonality & Wedding Compression",
  subtitle: "Demand & Event Yielding",
  iconName: "CalendarRange",
  description: "Manage massive surges during peak winter and royal wedding dates against severe summer troughs. We deploy dynamic minimum length-of-stay (MLOS) fences and unconstrained demand pacing to protect high-ADR dates.",
  metricLabel: "Strategy focus",
  metricValue: "Event pricing tiers & inventory fencing"
}, {
  id: 2,
  title: "Haveli & Comp-Set Undercutting",
  subtitle: "Real-time rate intelligence",
  iconName: "LineChart",
  description: "Track aggressive rate shifting across MakeMyTrip, Agoda, and Booking.com among boutique heritage properties. Real-time rate intelligence ensures you capture high-intent travelers without engaging in destructive price wars.",
  metricLabel: "Strategy focus",
  metricValue: "Dynamic competitor benchmarking & rate fences"
}, {
  id: 3,
  title: "Wholesaler Leakage & OTA Parity",
  subtitle: "Channel distribution",
  iconName: "ShieldAlert",
  description: "Prevent unvetted B2B static tour rates from leaking onto public OTAs and cutting your direct ADR. We tighten channel parity and build direct website booking drivers for high-yield luxury guests.",
  metricLabel: "Strategy focus",
  metricValue: "Contract rate fencing & direct booking conversion"
}];
const seasonalPhases = [{
  phase: "Peak Heritage & Weddings",
  months: "October – March",
  icon: Crown,
  tag: "High ADR",
  description: "High inbound international tourism, luxury palace buyouts, and big-ticket weddings. Yield management focuses on aggressive rate optimization, non-refundable deposits, and minimum stay rules."
}, {
  phase: "Summer Shoulder & Corporate",
  months: "April – June",
  icon: Sun,
  tag: "Yield Protection",
  description: "High daytime temperatures reduce leisure footfall. Strategy pivots toward corporate business, regional staycations, curated banquet packages, and guarded OTA promotions to prevent RevPAR freefalls."
}, {
  phase: "Monsoon & Weekend Drive-In",
  months: "July – September",
  icon: CloudRain,
  tag: "Pace Surge",
  description: "Surge in short-haul drive-in leisure from Delhi NCR, Haryana, and Gujarat alongside Teej festivities. Dynamic weekend rate spikes and direct packages capture high-intent road travelers."
}];
export default function Landscape() {
  const shouldReduceMotion = useReducedMotion();
  const containerVariants = {
    hidden: {},
    show: {
      transition: {
        staggerChildren: shouldReduceMotion ? 0 : 0.15
      }
    }
  };
  const cardVariants = {
    hidden: {
      opacity: 0,
      y: shouldReduceMotion ? 0 : 20
    },
    show: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.5,
        ease: "easeOut"
      }
    }
  };
  return <section id="hospitalitylandscapejaipur" data-nav="light" className="relative bg-background py-20 md:py-28 text-foreground">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-7xl">
        {/* Section Header */}
        <div className="mx-auto max-w-3xl text-center mb-16 md:mb-20">
          <Badge variant="outline" className="mb-4 border-secondary/40 text-secondary-foreground">
            Market Dynamics & Yield Protection
          </Badge>
          <h2 className="font-serif text-3xl sm:text-4xl md:text-5xl font-bold tracking-tight text-foreground">
            Navigating the hospitality landscape of Jaipur
          </h2>
          <p className="mt-4 text-base sm:text-lg text-muted-foreground leading-relaxed">
            Jaipur is a high-value, highly seasonal micro-market driven by lavish destination weddings, international heritage leisure, and volatile summer dips. Sunil Singh deploys disciplined, data-backed revenue strategies built specifically for Rajasthan&apos;s heritage havelis, luxury resorts, and upscale urban hotels.
          </p>
        </div>

        {/* 3-Column Card Grid */}
        <motion.div variants={containerVariants} initial="hidden" whileInView="show" viewport={{
        once: true,
        amount: 0.15
      }} className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8">
          {challenges.map((item, index) => <motion.div key={item.id} data-index={index} variants={cardVariants} className="h-full flex">
              <Card className="rounded-xl flex flex-col justify-between w-full border border-border bg-card shadow-sm hover:shadow-md transition-[border-color,box-shadow] duration-300">
                <CardHeader className="gap-3">
                  <div className="flex items-center justify-between">
                    <div className="inline-flex size-12 items-center justify-center rounded-xl bg-secondary/10 text-secondary-foreground">
                      {item.iconName === "CalendarRange" && <CalendarRange className="size-6 text-chart-2" />}
                      {item.iconName === "LineChart" && <LineChart className="size-6 text-chart-2" />}
                      {item.iconName === "ShieldAlert" && <ShieldAlert className="size-6 text-chart-2" />}
                    </div>
                    <span className="text-xs font-mono font-medium text-muted-foreground tracking-wider uppercase">
                      0{index + 1}
                    </span>
                  </div>
                  <div>
                    <span className="text-xs font-semibold uppercase tracking-wider text-secondary">
                      {item.subtitle}
                    </span>
                    <CardTitle className="font-serif text-xl sm:text-2xl font-bold mt-1.5 text-foreground leading-snug">
                      {item.title}
                    </CardTitle>
                  </div>
                </CardHeader>

                <CardContent className="space-y-6 pt-2">
                  <CardDescription className="text-sm leading-relaxed text-muted-foreground">
                    {item.description}
                  </CardDescription>

                  <div className="rounded-md border border-border/60 bg-muted/40 p-3.5 text-xs">
                    <p className="font-semibold text-foreground mb-1">
                      {item.metricLabel}
                    </p>
                    <p className="text-muted-foreground">
                      {item.metricValue}
                    </p>
                  </div>
                </CardContent>
              </Card>
            </motion.div>)}
        </motion.div>

        {/* Seasonality Strategy Block */}
        <div className="mt-16 md:mt-20 rounded-xl border border-border bg-card/60 p-6 sm:p-8 lg:p-10 shadow-sm">
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4 mb-8 pb-6 border-b border-border/70">
            <div>
              <span className="text-xs font-mono uppercase tracking-wider text-secondary font-semibold">
                Jaipur Demand Cycle Breakdown
              </span>
              <h3 className="font-serif text-2xl sm:text-3xl font-bold text-foreground mt-1">
                Seasonal Yield Architecture
              </h3>
            </div>
            <p className="text-sm text-muted-foreground max-w-lg">
              Understanding Jaipur&apos;s demand cadence is critical to preventing unneeded price slumps and maximizing peak compression revenue.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {seasonalPhases.map((phase, i) => {
            const PhaseIcon = phase.icon;
            return <div key={phase.phase} data-index={i} className="rounded-lg border border-border bg-background p-5 flex flex-col justify-between">
                  <div>
                    <div className="flex items-center justify-between gap-2 mb-3">
                      <div className="inline-flex size-9 items-center justify-center rounded-md bg-secondary/10 text-secondary-foreground">
                        <PhaseIcon className="size-4.5 text-chart-2" />
                      </div>
                      <Badge variant="outline" className="border-secondary/30 text-xs font-mono">
                        {phase.tag}
                      </Badge>
                    </div>
                    <h4 className="font-semibold text-base text-foreground">{phase.phase}</h4>
                    <p className="text-xs font-medium text-secondary mt-0.5 mb-2.5">{phase.months}</p>
                    <p className="text-xs text-muted-foreground leading-relaxed">{phase.description}</p>
                  </div>
                </div>;
          })}
          </div>
        </div>

        {/* Section Bottom CTA / Footnote */}
        <div className="mt-14 md:mt-18 pt-8 border-t border-border/70 flex flex-col sm:flex-row items-center justify-between gap-4">
          <p className="text-sm text-muted-foreground text-center sm:text-left">
            Need a tailored revenue assessment for your Jaipur hotel or boutique stay?
          </p>
          <Button asChild variant="default" className="rounded-full bg-primary text-primary-foreground hover:bg-primary/90">
            <Link to="#" className="inline-flex items-center gap-2">
              <span>Book a Revenue Audit</span>
              <ArrowUpRight className="size-4" />
            </Link>
          </Button>
        </div>
      </div>
    </section>;
}